import { isEmpty } from '@ember/utils';
import GradingMethod from 'linced-app/enums/grading-method';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

function getPossibleNumericGrades(module) {
  let schedule = module.get('gradeSchedule.numeric');
  let stepping =
    module.numericStepping || schedule.get('defaultNumericStepping');
  let start = schedule.get('range.start');
  let end = schedule.get('range.end');
  let grades = [];

  for (let i = start; i <= end; i += stepping) {
    grades.push({
      value: i,
      label: i,
    });
  }

  return grades;
}

export function getPossibleGrades(module) {
  switch (module.get('gradeSchedule.awardMethod')) {
    case GradingMethod.NUMERIC:
      return getPossibleNumericGrades(module);
    default:
      return module.get('gradeSchedule.select.values');
  }
}

export function gradesReducer(possibleGrades) {
  return (counts, grade) => {
    let awarded = [grade.awarded];
    let numericAwarded;
    switch (grade.gradeType) {
      case GradingMethod.MULTIPLE_SELECT:
        awarded = grade.multiSelectAwarded;
        break;

      case GradingMethod.NUMERIC:
        // Numeric grades need to be bucketed.
        numericAwarded = possibleGrades.find((possible, idx) => {
          let nextPossible = possible;
          if (idx < possibleGrades.length - 1) {
            nextPossible = possibleGrades[idx + 1];
          }
          switch (idx) {
            case 0:
              // Lowest possible grade.
              // Include everything below the next grade.
              return grade.awarded < nextPossible.value;
            case possibleGrades.length - 1:
              // Highest possible grade.  Include it and above.
              return grade.awarded >= possible.value;
            default:
              // All grades in between.  Include it, and everything above
              // it but below the next possible grade.
              return (
                possible.value <= grade.awarded &&
                grade.awarded < nextPossible.value
              );
          }
        });
        if (!isEmpty(numericAwarded)) {
          awarded = [numericAwarded.value];
        }
        break;
    }

    awarded.forEach((value) => {
      let count = findBy(counts, 'grade', value);
      if (isEmpty(count)) {
        count = {
          grade: value,
          learners: [grade.person],
        };
        counts.push(count);
      } else {
        count.learners.push(grade.person);
      }
    });
    return counts;
  };
}

// scaleNumericGrade calculates and applies the scale factor for a numeric Grade.
export function scaleNumericGrade(grade) {
  // Calculate numeric scale if applicable.
  let awarded = grade.numericAwarded ?? 0;
  if (awarded !== 0 && Math.floor(awarded) !== Number(awarded)) {
    // Determine the number of digits after the decimal place, if any.
    let bits = ('' + awarded).split('.');
    if (bits.length === 2) {
      let digits = bits[1].length;

      // Set scale
      grade.numericScale = -1 * digits;
      // Multiply awarded value by the inverse of the stored scale to get the value to store
      grade.numericAwarded = Math.round(
        grade.numericAwarded * Math.pow(10, digits),
      );
    }
  }

  return grade;
}

// getLabelForSelectAwarded is an internal function fo use in getGradeForDisplay.
function getLabelForSelectAwarded(module, awarded) {
  let options = module.get('gradeSchedule.select.values') ?? [];
  let selected = findBy(options, 'value', awarded);
  if (isEmpty(selected)) {
    return '';
  }
  return selected.get('label');
}

// getGradeForDisplay formats a grade for display, based on the grading method and schedule.
export function getGradeForDisplay(module, grade, plainText = false) {
  let option;
  let options;

  switch (module.get('gradeSchedule.awardMethod')) {
    case GradingMethod.NUMERIC:
      return grade.get('numericAwarded');

    case GradingMethod.SELECT:
      return getLabelForSelectAwarded(module, grade.get('selectAwarded'));

    case GradingMethod.MULTIPLE_SELECT:
      return (grade.get('multiSelectAwarded') ?? [])
        .map((val) => getLabelForSelectAwarded(module, val))
        .join(', ');

    case GradingMethod.ICON_SELECT:
      options = module.get('gradeSchedule.select.values') ?? [];
      option = findBy(options, 'value', grade.get('iconAwarded'));
      if (plainText) {
        if (isEmpty(option)) {
          return '';
        }
        return option.get('description');
      }
      if (!isEmpty(option)) {
        return { option, index: options.indexOf(option) };
      }
  }
}
